import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Themed confirmation modal — used in place of window.confirm() so we can
// match the neon dark theme. Click backdrop or Escape to cancel.
//
// Rendered through a React portal to document.body so the fixed-positioned
// backdrop and dialog are anchored to the viewport rather than to whatever
// stacking context the caller happens to mount inside (a transformed
// ancestor would otherwise pin the modal inside that ancestor and could
// even push it under sibling z-indexed elements).
export default function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmKind = 'danger',
  onConfirm,
  onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const isDanger = confirmKind === 'danger';
  const accent = isDanger ? 'var(--neon-magenta)' : 'var(--neon-blue)';
  const glow = isDanger ? 'var(--neon-magenta-dim)' : 'var(--neon-blue-dim)';
  const confirmClass = isDanger ? 'btn-pill btn-pill--danger' : 'btn-pill btn-pill--primary';

  return createPortal(
    <>
      <div className="confirm-modal-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        className="confirm-modal-layer"
        onClick={onClose}
      >
        <div
          className="confirm-modal-panel"
          onClick={(e) => e.stopPropagation()}
          style={{ '--cm-accent': accent, '--cm-glow': glow }}
        >
          <div className="confirm-modal-inner">
            <div className="confirm-modal-header">
              <h5 id="confirm-modal-title" className="confirm-modal-title">
                {title}
              </h5>
              <button
                type="button"
                className="confirm-modal-close"
                aria-label="Close"
                onClick={onClose}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="confirm-modal-body">
              {typeof body === 'string' ? <p>{body}</p> : body}
            </div>

            <div className="confirm-modal-footer">
              <button type="button" className="btn-ghost" onClick={onClose}>
                {cancelLabel}
              </button>
              <button
                type="button"
                className={confirmClass}
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
